/* eslint-disable react/no-multi-comp */
import React, { type FunctionComponent, useEffect, useState, useCallback } from 'react'
import { SVGIcon, colors, Text } from 'ic-snacks'
import { FormattedNumber, FormattedMessage } from 'react-intl'
import { css } from '@emotion/react'

import { TOPBAR_HEIGHT } from 'legacy/common/theme'
import { TableProductCell } from 'legacy/dash-components/table-product-cell/TableProductCell'
import ContentContainer from 'legacy/components/ContentContainer'
import AppHeader from 'legacy/components/AppHeader'
import Table from 'legacy/components/Table/Table'
import { type OrderDetailsApiResponse } from 'legacy/routes/orders/details/OrderDetails.types'
import ChargeCell from 'legacy/routes/orders/details/components/ChargeCell'
import { RefundCell } from 'legacy/routes/orders/details/components/RefundCell'
import { ReplacementModal } from 'legacy/routes/orders/details/components/ReplacementModal'
import OrderDetailsBreadcrumbs from 'legacy/routes/orders/details/components/OrderDetailsBreadcrumbs'

import {
  getInitialFromData,
  getCurrentFromData,
} from 'legacy/routes/orders/details/utils/orderDetails.utils'
import { useTrackEvent } from 'utils/events/hooks'

const replacementIconStyles = css({ cursor: 'pointer' })

const rightContentStyles = css({
  flexGrow: 1,
  borderLeft: `1px solid ${colors.GRAY_93}`,
})

const getTrProps = () => ({ style: { height: '85px', padding: '0 20px' } })

const getTableProps = () => ({
  style: {
    padding: '0',
    margin: '0',
    background: 'white',
    borderRadius: '0px',
    border: 'none',
    height: `calc(100vh - ${TOPBAR_HEIGHT} - 60px)`,
  },
})

export type OrderDetailsProps = OrderDetailsApiResponse

type OrderDetailsState = {
  initial?: number
  current?: number
  showReplacement: boolean
  replacementName?: string
  replacementImageUrl?: string
  orderId?: number
  replacementProductId?: string
}

export const OrderDetails: FunctionComponent<OrderDetailsProps> = ({ data, meta }) => {
  const [state, setState] = useState<OrderDetailsState>({
    showReplacement: false,
  })
  useTrackEvent({
    id: 'orders.order_details.viewed',
    description: 'Viewed the details of a specific order in the orders section',
  })

  useEffect(() => {
    if (data?.items?.length > 0) {
      setState((prevState: OrderDetailsState) => {
        return {
          ...prevState,
          initial: getInitialFromData(data),
          current: getCurrentFromData(data),
          orderId: data.order.order_id,
        }
      })
    }
  }, [data])

  const showReplacement = useCallback(
    (name: string, imageUrl: string, replacementProductId: string) => {
      setState((prevState: OrderDetailsState) => {
        return {
          ...prevState,
          showReplacement: true,
          replacementName: name,
          replacementImageUrl: imageUrl,
          replacementProductId,
        }
      })
    },
    []
  )

  const hideReplacement = useCallback(() => {
    setState((prevState: OrderDetailsState) => {
      return {
        ...prevState,
        showReplacement: false,
      }
    })
  }, [])

  /**
   * @refactor
   *  columns function needs to be refactored into four smaller
   *  components - Product, Initial, Current, Refund and Replacement
   *  columns. This refactoring will require some pre-work with Table
   *  components, which is currently in JSX.
   */
  const columns = () => {
    const columns = [
      {
        header: 'Product',
        Cell: ({ original }) => (
          <TableProductCell
            productId={original.product_id}
            productName={original.name}
            imageUrl={original.image_url}
          />
        ),
        Footer: <FormattedMessage id="orders.details.total" />,
      },
      {
        header: 'Initial',
        width: 200,
        Cell: ({ original }) => (
          <ChargeCell
            quantity={original.initial_qty}
            unitPrice={original.initial_unit_price}
            charge={original.initial_charge}
          />
        ),
        Footer: (
          <Text variant="T.16">
            <FormattedNumber value={state.initial} style="currency" currency="USD" />
          </Text>
        ),
      },
      {
        header: 'Current',
        width: 200,
        Cell: ({ original }) => (
          <ChargeCell
            quantity={original.current_qty}
            unitPrice={original.current_unit_price}
            charge={original.current_charge}
          />
        ),
        Footer: (
          <Text variant="T.16">
            <FormattedNumber value={state.current} style="currency" currency="USD" />
          </Text>
        ),
      },
      {
        header: 'Refund',
        width: 100,
        Cell: ({ original }) => <RefundCell refund={original.refund} />,
      },
      {
        header: '',
        width: 50,
        Cell: ({ original }) =>
          original.replaced && (
            <SVGIcon
              css={replacementIconStyles}
              name="replacement"
              size="standard"
              color={colors.GREEN_500}
              onClick={() => {
                showReplacement(
                  original.replacement_name,
                  original.replacement_image_url,
                  original.replacement_product_id
                )
              }}
            />
          ),
      },
    ]
    return columns
  }

  return (
    <div>
      <AppHeader title={<OrderDetailsBreadcrumbs orderId={state.orderId} />} />
      <ContentContainer style={{ margin: 0, padding: 0 }}>
        <div css={rightContentStyles}>
          <Table
            columns={columns()}
            isLoading={false}
            isError={false}
            getTrProps={getTrProps}
            getTableProps={getTableProps}
            data={data ? data.items : data}
            meta={meta}
            noDataText="No Orders"
            paginated
          />
        </div>
      </ContentContainer>
      {state.showReplacement && (
        <ReplacementModal
          productId={state.replacementProductId}
          productName={state.replacementName}
          imageUrl={state.replacementImageUrl}
          onClose={hideReplacement}
        />
      )}
    </div>
  )
}
